import { UserCard } from "@/components/cards";
import { useAssignmentReferenceBookingsModalContext } from "@/modals/partners/assignment-reference-bookings-modal/context";
import { useOrganizationMembersShallow } from "@/modals/partners/assignment-reference-bookings-modal/hooks/use-organization-members-shallow";
import { Select, type SelectOption } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { AssignmentReferenceBookingsSchema } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";

/** Props Interface */
interface AssignmentReferenceBookingPersonInputProps {
	index: number;
	canChangePerson: boolean;
	fullName: string;
}

type ShallowMember = { id: string; name: string };

function getOptions(members: ShallowMember[]): SelectOption<ShallowMember, string>[] {
	return members.map((member) => ({
		value: member.id,
		label: member.name,
		item: member,
	}));
}

export function AssignmentReferenceBookingPersonInput({
	index,
	canChangePerson,
	fullName,
}: AssignmentReferenceBookingPersonInputProps) {
	/** Context */
	const methods = useFormContext<AssignmentReferenceBookingsSchema>();
	const { partnerOrgId } = useAssignmentReferenceBookingsModalContext();

	/** Hooks */
	const { t } = useTranslation();
	const members = useOrganizationMembersShallow(partnerOrgId);

	/** Guard */
	// A locked row prints the person. The select would label the value from the partner's member
	// list, which need not carry a name the host reads through the booking alone, and the
	// partner's statement is not for the host to pick anyway.
	if (!canChangePerson) {
		return <p className="h-9 h-stack font-semibold">{fullName || t("partners.no_person_named")}</p>;
	}

	/** Render */
	return (
		<Select
			control={methods.control}
			name={`bookings.${index}.userId`}
			options={getOptions(members)}
			isRequired={false}
			label={undefined}
			itemNotFoundLabel={t("general.not_found")}
			renderOption={(item) => (
				<UserCard
					userId={item.id}
					withPopover={false}
					variant="plain"
				/>
			)}
		/>
	);
}
